import { FileText } from "lucide-react";
import { type ReactNode } from "react";

import { m } from "@recobra/i18n/messages";
import type { DocumentType } from "@recobra/schemas/document";
import { Card, CardContent, CardHeader, CardTitle } from "@recobra/ui/card";
import { Skeleton } from "@recobra/ui/skeleton";

import { UploadEntryList } from "./UploadEntryList";
import type { UploadRow } from "./UploadTypes";

export const DocumentListCard = ({
  isLoading,
  rows,
  action,
  showAnalysisDetails,
  groupByType,
  defaultOpenSections,
  onRemove,
  removeDisabled,
  onOverride,
  overridePending,
}: {
  isLoading: boolean;
  rows: UploadRow[];
  action?: ReactNode;
  showAnalysisDetails?: boolean;
  groupByType?: boolean;
  defaultOpenSections?: "priority" | "all" | "none";
  onRemove?: (row: UploadRow) => void;
  removeDisabled?: (row: UploadRow) => boolean;
  onOverride?: (row: UploadRow, documentType: DocumentType) => void;
  overridePending?: boolean;
}) => {
  let content: ReactNode;
  if (isLoading) {
    content = (
      <div className="flex flex-col gap-2">
        {[1, 2, 3].map((item) => (
          <Skeleton key={item} className="h-16 w-full" />
        ))}
      </div>
    );
  } else if (rows.length === 0) {
    content = (
      <p className="py-4 text-center text-sm text-muted-foreground">{m.doc_list_empty()}</p>
    );
  } else {
    content = (
      <UploadEntryList
        rows={rows}
        defaultOpenSections={defaultOpenSections}
        showAnalysisDetails={showAnalysisDetails}
        groupByType={groupByType}
        onRemove={onRemove}
        removeDisabled={removeDisabled}
        onOverride={onOverride}
        overridePending={overridePending}
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-foreground">
              <FileText className="size-3.5" />
            </div>
            <CardTitle>{m.doc_list_title()}</CardTitle>
          </div>
          {action}
        </div>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
};
